// src/components/themes/default/store/branch/BranchProductReviews.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import {
  Star,
  CheckCircle2,
  ThumbsUp,
  MessageSquare,
  CornerDownRight,
  Send,
  Loader2,
  PlusCircle,
  X,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";

interface BranchProductReviewsProps {
  productId: number | string;
  productName?: string;
  branchId?: number | string;
  branchName?: string;
  subdomain?: string;
  primaryColor?: string;
  branchUser?: any;
  onOpenAuth?: () => void;
  isLight?: boolean;
}

export default function BranchProductReviews({
  productId,
  productName = "Product",
  branchId,
  branchName = "Branch Outlet",
  subdomain = "nextlook",
  primaryColor = "#059669",
  branchUser = null,
  onOpenAuth = () => {},
  isLight = true,
}: BranchProductReviewsProps) {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 🌟 Active Customer Identity Detection
  const activeUser = useMemo(() => {
    if (branchUser) return branchUser;
    if (typeof window !== "undefined") {
      try {
        const bId = branchId;
        const localData =
          (bId && localStorage.getItem(`branch_customer_${bId}`)) ||
          localStorage.getItem("customer_user");
        if (localData) return JSON.parse(localData);
      } catch {}
    }
    return null;
  }, [branchId, branchUser]);

  const [summary, setSummary] = useState<{
    total_reviews: number;
    average_rating: number;
    distribution: Record<number, number>;
  }>({
    total_reviews: 0,
    average_rating: 5,
    distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  });

  const [rating, setRating] = useState<number>(5);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewContent, setReviewContent] = useState("");

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  const fetchReviewsData = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const branchParam = branchId ? `&branch_id=${branchId}` : "";

      const res = await fetch(
        getApiUrl(`/api/v1/reviews/?product=${productId}${branchParam}`)
      );
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setReviews(list);
      }

      const summaryRes = await fetch(
        getApiUrl(`/api/v1/reviews/product-summary/${productId}/`)
      );
      if (summaryRes.ok) {
        const sData = await summaryRes.json();
        setSummary({
          total_reviews: sData.total_reviews || 0,
          average_rating: sData.average_rating || 5,
          distribution: sData.distribution || { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        });
      }
    } catch (err) {
      console.error("Error fetching reviews:", err);
    } finally {
      setLoading(false);
    }
  }, [branchId, getApiUrl, productId]);

  useEffect(() => {
    fetchReviewsData();
  }, [fetchReviewsData]);

  // 🌟 Customer Review Submit with Tokens & Name
  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reviewContent.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Review Required",
        text: "Apnar motamot likhun.",
        confirmButtonColor: primaryColor,
      });
      return;
    }

    setSubmitting(true);
    try {
      let customerToken = "";
      if (typeof window !== "undefined") {
        customerToken =
          (branchId && localStorage.getItem(`branch_customer_token_${branchId}`)) ||
          localStorage.getItem("customer_token") ||
          "";
      }

      const displayName =
        activeUser?.first_name ||
        activeUser?.name ||
        (activeUser?.email ? activeUser.email.split("@")[0] : "");

      const payload: any = {
        product: productId,
        rating,
        title: reviewTitle.trim() || `${rating} Star Review`,
        content: reviewContent.trim(),
        customer_id: activeUser?.id || null,
        reviewer_name: displayName,
        phone: activeUser?.phone || "",
        email: activeUser?.email || "",
      };

      const res = await fetch(getApiUrl("/api/v1/reviews/"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(branchId ? { "X-Branch-ID": String(branchId) } : {}),
          ...(customerToken ? { Authorization: `Bearer ${customerToken}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setIsModalOpen(false);
        setReviewTitle("");
        setReviewContent("");
        setRating(5);
        await fetchReviewsData();

        Swal.fire({
          icon: "success",
          title: "Dhonnobad!",
          text: `Apnar review '${displayName || "Customer"}' hishebe joma hoyeche.`,
          confirmButtonColor: primaryColor,
        });
      } else {
        throw new Error("Submission failed");
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Bartho Hoyeche",
        text: "Review joma dewa shombhob hoyni. Abar cheshta korun.",
        confirmButtonColor: primaryColor,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoteHelpful = async (reviewId: number) => {
    try {
      const res = await fetch(getApiUrl(`/api/v1/reviews/${reviewId}/helpful/`), {
        method: "POST",
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setReviews((prev) =>
          prev.map((r) =>
            r.id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r
          )
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const totalCount = summary.total_reviews || reviews.length || 0;
  const avgRatingDisplay = (summary.average_rating || 5).toFixed(1);

  return (
    <div className="w-full space-y-8 py-4 font-sans text-slate-800">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center p-6 sm:p-8 rounded-3xl bg-slate-50 border border-slate-200">
        <div className="md:col-span-5 flex flex-col items-center justify-center text-center p-4 border-b md:border-b-0 md:border-r border-slate-200">
          <div className="flex items-center gap-1.5 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className="w-6 h-6 fill-amber-400 text-amber-400 drop-shadow-xs"
              />
            ))}
          </div>

          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight font-mono">
            {avgRatingDisplay} <span className="text-xl font-bold text-slate-400">/ 5</span>
          </h2>

          <div className="flex items-center gap-1.5 mt-2 text-xs font-bold text-emerald-600">
            <ShieldCheck className="w-4 h-4" />
            <span>Verified Outlet Feedback</span>
          </div>

          <p className="text-xs text-slate-500 mt-1 max-w-xs">
            Verified customer purchases from {branchName} outlet store.
          </p>

          <button
            onClick={() => setIsModalOpen(true)}
            style={{ backgroundColor: primaryColor }}
            className="mt-5 px-6 py-2.5 rounded-2xl text-white text-xs font-black tracking-wide inline-flex items-center gap-2 shadow-lg shadow-emerald-600/20 hover:opacity-90 active:scale-95 transition-all cursor-pointer"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Write a Review</span>
          </button>
        </div>

        <div className="md:col-span-7 space-y-2.5 px-2 sm:px-6">
          <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3">
            Rating Breakdown ({totalCount} Reviews)
          </h4>

          {[5, 4, 3, 2, 1].map((starNum) => {
            const count = summary.distribution[starNum] || 0;
            const percentage =
              totalCount > 0 ? Math.round((count / totalCount) * 100) : starNum === 5 ? 100 : 0;

            return (
              <div key={starNum} className="flex items-center gap-3 text-xs font-bold font-mono">
                <span className="w-12 text-slate-600 flex items-center gap-1 shrink-0">
                  <span>{starNum}</span>
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                </span>

                <div className="flex-1 h-3 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    style={{
                      width: `${percentage}%`,
                      backgroundColor:
                        starNum >= 4 ? primaryColor : starNum === 3 ? "#f59e0b" : "#ef4444",
                    }}
                    className="h-full rounded-full transition-all duration-700"
                  />
                </div>

                <span className="w-12 text-right text-slate-500 text-[11px] shrink-0">
                  {percentage}%
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-3 border-slate-200">
          <h3 className="text-base font-black text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-emerald-600" />
            <span>Customer Opinions & Reviews</span>
          </h3>

          <span className="text-xs font-mono font-bold text-slate-400">
            Showing {reviews.length} Verified Reviews
          </span>
        </div>

        {loading ? (
          <div className="py-20 text-center">
            <Loader2 style={{ color: primaryColor }} className="w-7 h-7 animate-spin mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-400">Loading verified reviews...</p>
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-12 text-center rounded-3xl border border-dashed border-slate-300 space-y-3">
            <Sparkles className="w-8 h-8 text-amber-500 mx-auto opacity-70" />
            <h4 className="text-sm font-black text-slate-800">Ekhono kono review dewa hoyni</h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Ei product-ti {branchName} theke purchase kore prothom review prodankari hon!
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              style={{ backgroundColor: primaryColor }}
              className="px-5 py-2 rounded-xl text-white text-xs font-bold shadow-md cursor-pointer inline-flex items-center gap-1.5"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              <span>Prothom Review Din</span>
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {reviews.map((rev) => (
              <div key={rev.id} className="py-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center font-black text-xs font-mono uppercase">
                      {rev.customer_display_name?.charAt(0) || "U"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-xs font-black text-slate-900">
                          {rev.customer_display_name || "Verified Customer"}
                        </h4>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="w-2.5 h-2.5" />
                          <span>Verified Purchase</span>
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {new Date(rev.created_at).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-3.5 h-3.5 ${
                          s <= (rev.rating || 5)
                            ? "fill-amber-400 text-amber-400"
                            : "text-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                <div className="text-xs leading-relaxed text-slate-700 pl-12 space-y-1">
                  {rev.title && <p className="font-black text-slate-900">{rev.title}</p>}
                  <p className="whitespace-pre-wrap">{rev.content}</p>
                </div>

                <div className="flex items-center gap-4 pl-12 pt-1 text-[11px] text-slate-400 font-bold">
                  <button
                    onClick={() => handleVoteHelpful(rev.id)}
                    className="flex items-center gap-1 hover:text-emerald-600 transition-colors cursor-pointer"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" />
                    <span>Helpful ({rev.helpful_count || 0})</span>
                  </button>
                </div>

                {rev.replies && rev.replies.length > 0 && (
                  <div className="ml-12 mt-3 p-3.5 rounded-2xl bg-emerald-50/70 border border-emerald-200/80 space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-black text-emerald-800">
                      <CornerDownRight className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{branchName} Outlet Reply</span>
                    </div>
                    {rev.replies.map((reply: any, rIdx: number) => (
                      <p key={rIdx} className="text-xs text-slate-700 pl-5">
                        {reply.content}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 sm:p-8 shadow-2xl border border-slate-200 space-y-5 relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute right-5 top-5 p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div>
              <span
                style={{ color: primaryColor }}
                className="text-[10px] font-black uppercase tracking-widest font-mono block mb-1"
              >
                {branchName} Feedback
              </span>
              <h3 className="text-xl font-black text-slate-900">
                Write a Review for {productName}
              </h3>

              {/* 🌟 Logged-in Customer Badge */}
              {activeUser ? (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-900">
                  <User className="w-3.5 h-3.5 text-emerald-600" />
                  <span>
                    Reviewing as: <strong>{activeUser.first_name || activeUser.name || activeUser.email}</strong>
                  </span>
                </div>
              ) : (
                <p className="text-xs text-slate-500 mt-0.5">
                  Apnar kenakatar obhiggota onno kretader shathe share korun.
                </p>
              )}
            </div>

            <form onSubmit={handleSubmitReview} className="space-y-4 text-xs font-bold">
              <div className="space-y-1.5 p-3.5 rounded-2xl bg-slate-50 border border-slate-200 text-center">
                <label className="text-[11px] font-black uppercase tracking-wide text-slate-500 block">
                  Select Rating
                </label>
                <div className="flex items-center justify-center gap-2 py-1">
                  {[1, 2, 3, 4, 5].map((starVal) => {
                    const isFilled = starVal <= (hoverRating || rating);
                    return (
                      <button
                        type="button"
                        key={starVal}
                        onMouseEnter={() => setHoverRating(starVal)}
                        onMouseLeave={() => setHoverRating(0)}
                        onClick={() => setRating(starVal)}
                        className="p-1 transition-transform hover:scale-125 cursor-pointer"
                      >
                        <Star
                          className={`w-7 h-7 ${
                            isFilled
                              ? "fill-amber-400 text-amber-400"
                              : "text-slate-300"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                <span className="text-xs font-mono font-black text-amber-600 block">
                  {rating === 5
                    ? "5 Star - Chomotkar Quality!"
                    : rating === 4
                    ? "4 Star - Bhalo Ponno"
                    : rating === 3
                    ? "3 Star - Gorporota"
                    : rating === 2
                    ? "2 Star - Ashanurup Noy"
                    : "1 Star - Khub Kharap"}
                </span>
              </div>

              <div className="space-y-1">
                <label className="text-slate-600">Review Title (Optional)</label>
                <input
                  type="text"
                  placeholder="Jemon: Oshadharon finishing ba premium quality"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-xs font-medium outline-none focus:border-emerald-500 bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-slate-600">Detailed Feedback *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Ponyer gunogoto man, delivery service o apnar obhiggota likhun..."
                  value={reviewContent}
                  onChange={(e) => setReviewContent(e.target.value)}
                  className="w-full p-4 rounded-xl border border-slate-200 text-xs font-medium outline-none focus:border-emerald-500 bg-white"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{ backgroundColor: primaryColor }}
                  className="px-6 py-2.5 rounded-xl text-white font-black inline-flex items-center gap-2 shadow-lg hover:opacity-90 active:scale-95 transition-all cursor-pointer disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5" />
                  )}
                  <span>Submit Review</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}